import { useMutation } from "@tanstack/react-query";
import { type Api, insertWorkoutItem } from "api-client";
import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "ui";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import {
  type ExerciseDraftViewProps,
  useDraftWorkoutItemStackContext,
} from "src/workouts/DraftWorkoutItemView.shared";
import { ExerciseCombobox } from "src/workouts/ExerciseCombobox";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import { scrollWorkoutItemIntoView } from "src/workouts/workoutItemScroll";

export const ExerciseDraftView = observer(function ExerciseDraftView(
  props: ExerciseDraftViewProps,
) {
  const { t } = useTranslation();
  const toast = useToast();
  const workoutEditStore = useWorkoutEditStore();
  const draftItemStackContext = useDraftWorkoutItemStackContext();

  const [isOpen, setIsOpen] = useState(false);

  const workoutItem = workoutEditStore.getWorkoutItemById(props.workoutItemId);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsOpen(true);
    }, 200);
    draftItemStackContext.stack.push("exercise-combobox");
    return () => {
      draftItemStackContext.stack.pop();
      clearTimeout(timeoutId);
    };
  }, []);

  const { startSaving } = useAuthenticatedNavigationContext();

  const insertWorkoutItemMutation = useMutation({
    mutationFn: async ({
      draftItemId,
      ...params
    }: Api.InsertWorkoutItem.Input & { draftItemId: string }) => {
      const stopSaving = startSaving();

      const response = await insertWorkoutItem({
        path: { workoutId: params.path.workoutId },
        body: params.body,
      }).catch(() => {
        return null;
      });

      return {
        data: response?.data.data ?? null,
        draftItemId: draftItemId,
        stopSaving,
      };
    },
    onSettled: (response, _, variables) => {
      workoutEditStore.removeItem(variables.draftItemId);

      if (response?.data) {
        workoutEditStore.insertWorkoutItem(response.data.order, response.data);
        void scrollWorkoutItemIntoView({
          workoutItemId: response.data.id,
        });
        response.stopSaving();
      } else {
        toast.toast({
          title: t("editWorkout.addExerciseError.title"),
          description: t("editWorkout.addExerciseError.description"),
          variant: "destructive",
        });
        response?.stopSaving("error");
      }
    },
  });

  function handleOpenChange(open: boolean) {
    if (open) {
      draftItemStackContext.stack.push("exercise-combobox");
    } else {
      draftItemStackContext.stack.pop();
    }

    setIsOpen(open);

    if (!open && workoutItem.exercise === null) {
      workoutEditStore.removeItem(workoutItem.id);
    }
  }

  return (
    <div className={"flex items-center gap-2"}>
      <ExerciseCombobox
        disabled={insertWorkoutItemMutation.isPending}
        isOpen={isOpen}
        setIsOpen={handleOpenChange}
        value={workoutItem.exercise}
        onChange={(exercise) => {
          workoutEditStore.updateWorkoutItem(workoutItem.id, {
            exercise: { ...exercise, exerciseAttributes: [] },
          });
          insertWorkoutItemMutation.mutate({
            path: { workoutId: workoutEditStore.workout.id },
            body: {
              at: props.index,
              parentId: workoutItem.parentId,
              exerciseId: exercise.id,
            },
            draftItemId: workoutItem.id,
          });
        }}
      />
      {insertWorkoutItemMutation.isPending ? (
        <Icon
          name="tabler-loader-2"
          className={"animate-spin text-muted-foreground"}
        ></Icon>
      ) : null}
    </div>
  );
});
